import React, { useState, useEffect } from 'react';
import { Button, Form, Card, Alert, Row, Col, InputGroup, Badge } from 'react-bootstrap';
import { Save, X, Plus, DollarSign, Tag, ShoppingBag, FileText, Layers, Upload, Image as ImageIcon, Trash } from 'lucide-react';
import useForm from '../../hooks/useForm';
import { useNavigate } from 'react-router-dom'; // Pour la redirection
import { toast } from 'react-toastify'; // Pour les notifications
import { createProduct, getProductById, updateProduct, addProductImage, removeProductImage } from '../../api/products';
import { uploadImageToWordPress, createWordPressProduct } from '../../utils/uploadImageToWordPress';

const ProductForm = ({ productId, onCancel, onSuccess }) => {
    const isEditMode = !!productId;
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [imageUploading, setImageUploading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [images, setImages] = useState([]);
    const [imageFiles, setImageFiles] = useState([]);
    const [mainImageIndex, setMainImageIndex] = useState(0);
    const [syncingWithWP, setSyncingWithWP] = useState(false);

    const initialValues = {
        name: '',
        description: '',
        dimensions: '',
        regular_price: '',
        stock_quantity: 0,
        sku: '',
        category: '',
        // Nouvel attribut pour stocker l'ID du produit WordPress
        wpProductId: null
    };

    const { values, setValues, handleChange, errors, validateForm } = useForm(initialValues);

    useEffect(() => {
        if (isEditMode) {
            fetchProductData();
        }
    }, [productId]);

    const fetchProductData = async () => {
        try {
            setLoading(true);
            const productData = await getProductById(productId);

            setValues({
                name: productData.name.trim(),
                description: productData.description || '',
                dimensions: productData.dimensions || '',
                regular_price: productData.regular_price.toString(),
                stock_quantity: productData.stock_quantity,
                sku: productData.sku.trim(),
                category: productData.category || '',
                wpProductId: productData.wpProductId || null
            });

            if (productData.images && productData.images.length > 0) {
                setImages(productData.images);
                const mainIndex = productData.images.findIndex(img => img.mainImage);
                if (mainIndex !== -1) {
                    setMainImageIndex(mainIndex);
                }
            }
        } catch (err) {
            setError('Failed to load product data. Please try again.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // Remplacer la fonction getFullImageUrl dans ProductForm.jsx par celle-ci :
    const getFullImageUrl = (path) => {
        if (!path) return null;

        // Si le chemin commence déjà par http ou https, c'est une URL complète
        if (path.startsWith('http://') || path.startsWith('https://')) {
            return path;
        }

        // API_BASE_URL est utilisé dans d'autres parties de votre code
        //const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://senoscar.site';
        //const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:8080';
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://apps.prod.atlanticds-sn.com/senoscar';


        // Toujours ajouter le préfixe /uploads/ pour toutes les images
        return `${API_BASE_URL}/uploads/${path}`;
        //return `${API_BASE_URL}/products/${path}`;

    };

    const validationSchema = {
        name: {
            required: true,
            errorMessage: 'Product name is required'
        },
        regular_price: {
            required: true,
            pattern: /^\d+(\.\d{1,2})?$/,
            errorMessage: 'Valid price is required (e.g., 19.99)'
        },
        sku: {
            required: true,
            errorMessage: 'SKU is required'
        }
    };

    // Fonction séparée pour la synchronisation WordPress
    //  const syncWithWordPress = async (productData, imageFiles, productId) => {
    //     try {

    //          // Vérification de basePrice
    //             if (productData.regular_price == null) { 
    //                 throw new Error('Le prix de base (basePrice) est manquant pour ce produit.');
    //             }
    //         // Télécharger d'abord toutes les images vers WordPress
    //         const wpImageIds = [];
    //         for (const file of imageFiles) {
    //             const wpImageResponse = await uploadImageToWordPress(file);
    //             wpImageIds.push(wpImageResponse.id);
    //         }

    //         // Créer ou mettre à jour le produit WordPress avec les IDs d'images téléchargées
    //         const wpProduct = await createWordPressProduct(productData, wpImageIds);

    //         // Mettre à jour notre produit avec l'ID WordPress pour référence future
    //         await updateProduct(productId, {
    //             wpProductId: wpProduct.id,
    //             name: productData.name,
    //             description: productData.description,
    //             category: productData.category,
    //             dimensions: productData.dimensions,
    //             sku: productData.sku,
    //             regular_price: productData.regular_price // (ou regular_price selon ton backend)
    //           });


    //         // Notification de succès
    //         toast.success('Produit synchronisé avec succès dans WordPress!');
    //         return wpProduct.id;
    //     } catch (err) {
    //         console.error('Erreur lors de la synchronisation avec WordPress:', err);
    //         // Notification d'erreur
    //         toast.error(`Échec de la synchronisation avec WordPress: ${err.message || 'Erreur inconnue'}`);
    //         return null;
    //     }
    // };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccess('');

        // Validate form
        const isValid = validateForm(validationSchema);
        if (!isValid) {
            setLoading(false);
            return;
        }

        try {
            const productData = {
                ...values,
                regular_price: parseFloat(values.regular_price),
                stock_quantity: parseInt(values.stock_quantity, 10)
            };

            let savedProductId;

            if (isEditMode) {
                // Update existing product
                await updateProduct(productId, productData);
                savedProductId = productId;
                setSuccess('Produit mis à jour avec succès!');

                // Redirection immédiate
                if (onSuccess) onSuccess();
                else navigate('/products'); // Assurez-vous que ce chemin correspond à votre route de liste de produits

                // Si nous avons de nouvelles images à synchroniser, faire en arrière-plan
                // if (imageFiles.length > 0) {
                //toast.info('Synchronisation avec WordPress en cours...');
                // syncWithWordPress(productData, imageFiles, savedProductId);
                // }
            } else {
                // Create new product in our system first
                const createdProduct = await createProduct(productData, imageFiles, mainImageIndex);
                savedProductId = createdProduct.id;

                console.log('Le produit cree avec succes ')

                // Redirection immédiate après création dans notre base
                toast.success('Produit créé avec succès!');
                if (onSuccess) onSuccess();
                else navigate('/products'); // Redirection vers la liste des produits

                // Démarrer la synchronisation WordPress en arrière-plan
                //toast.info('Synchronisation avec WordPress en cours...');
                // syncWithWordPress(productData, imageFiles, savedProductId);
            }
        } catch (err) {
            setError(err.message || 'Une erreur est survenue. Veuillez réessayer.');
            console.error(err);
            setLoading(false);
        }
    };


    const handleImageChange = (e) => {
        const files = Array.from(e.target.files);

        // Validate file types
        const validFiles = files.filter(file => {
            const fileType = file.type;
            return fileType === 'image/jpeg' || fileType === 'image/png' || fileType === 'image/webp';
        });

        if (validFiles.length !== files.length) {
            alert('Only JPEG, PNG, and WebP images are allowed');
        }

        // Create preview URLs
        const filePreviews = validFiles.map(file => ({
            url: URL.createObjectURL(file),
            filename: file.name,
            file: file
        }));

        setImageFiles([...imageFiles, ...validFiles]);
        setImages([...images, ...filePreviews.map((preview, index) => ({
            id: `new-${Date.now()}-${index}`,
            path: preview.url,
            mainImage: false,
            isNew: true,
            file: preview.file
        }))]);
    };

    const handleRemoveImage = async (index) => {
        const imageToRemove = images[index];

        if (isEditMode && !imageToRemove.isNew) {
            try {
                setImageUploading(true);
                setError('');

                console.log("Suppression de l'image:", imageToRemove.id, "du produit:", productId);
                await removeProductImage(productId, imageToRemove.id);
                console.log("Image supprimée avec succès");

                // Mettre à jour l'état local sans recharger toutes les données
                const newImages = [...images];
                newImages.splice(index, 1);
                setImages(newImages);

                // Mettre à jour l'index de l'image principale si nécessaire
                if (mainImageIndex === index) {
                    setMainImageIndex(newImages.length > 0 ? 0 : -1);
                } else if (mainImageIndex > index) {
                    setMainImageIndex(mainImageIndex - 1);
                }

                setSuccess('Image supprimée avec succès!');
            } catch (err) {
                console.error("Erreur détaillée lors de la suppression:", err);
                setError(`Impossible de supprimer l'image: ${err.message || 'Erreur inconnue'}`);
            } finally {
                setImageUploading(false);
            }
        } else {
            // Pour les nouvelles images non encore sauvegardées, juste mettre à jour l'état
            const newImages = [...images];
            newImages.splice(index, 1);
            setImages(newImages);

            // Aussi supprimer des imageFiles
            if (imageToRemove.isNew) {
                const newImageFiles = [...imageFiles];
                const fileIndex = newImageFiles.findIndex(file =>
                    file.name === imageToRemove.file.name && file.size === imageToRemove.file.size
                );
                if (fileIndex !== -1) {
                    newImageFiles.splice(fileIndex, 1);
                    setImageFiles(newImageFiles);
                }
            }

            // Mettre à jour l'index de l'image principale si nécessaire
            if (mainImageIndex === index) {
                setMainImageIndex(newImages.length > 0 ? 0 : -1);
            } else if (mainImageIndex > index) {
                setMainImageIndex(mainImageIndex - 1);
            }
        }
    };

    const handleUploadImage = async (e) => {
        e.preventDefault();
        const files = Array.from(e.target.files);

        if (!files.length || !isEditMode) return;

        try {
            setImageUploading(true);
            setError('');

            const file = files[0];
            const isMain = images.length === 0; // Si c'est la première image, la définir comme principale

            console.log("Envoi de l'image:", file.name, "taille:", file.size, "type:", file.type);

            const response = await addProductImage(productId, file, isMain);
            console.log("Réponse du serveur après ajout d'image:", response);

            // Au lieu d'appeler fetchProductData, mettre à jour manuellement l'état
            // Cela évite un rechargement complet qui peut échouer
            if (response && response.id) {
                const newImage = {
                    id: response.id,
                    path: response.path,
                    mainImage: isMain,
                    description: null
                };

                setImages(prevImages => [...prevImages, newImage]);

                if (isMain) {
                    setMainImageIndex(prevImages => prevImages.length);
                }

                // Ajouter également l'image à WordPress si le produit est déjà synchronisé
                if (values.wpProductId) {
                    try {
                        const response = await uploadImageToWordPress(file);
                        console.log("Image également synchronisée avec WordPress ", response);
                    } catch (wpErr) {
                        console.error("Erreur lors de l'ajout de l'image à WordPress:", wpErr);
                    }
                }
            } else {
                // Si la réponse n'est pas au bon format, rechargeons quand même les données
                await fetchProductData();
            }

            setSuccess('Image ajoutée avec succès!');
        } catch (err) {
            console.error("Erreur détaillée lors de l'upload:", err);
            setError(`Impossible d'ajouter l'image: ${err.message || 'Erreur inconnue'}`);
        } finally {
            setImageUploading(false);
            // Réinitialiser l'input file pour pouvoir sélectionner le même fichier à nouveau
            e.target.value = null;
        }
    };

    const setAsMainImage = (index) => {
        setMainImageIndex(index);

        // If in edit mode, update the mainImage property for all images
        if (isEditMode) {
            const updatedImages = images.map((img, i) => ({
                ...img,
                mainImage: i === index
            }));
            setImages(updatedImages);
        }
    };

    return (
        <Card className="shadow-sm">
            <Card.Header>
                <h5 className="mb-0">{isEditMode ? 'Modifier Produit' : 'Ajouter un nouveau produit'}</h5>
            </Card.Header>
            <Card.Body>
                {error && <Alert variant="danger">{error}</Alert>}
                {success && <Alert variant="success">{success}</Alert>}

                <Form onSubmit={handleSubmit}>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Nom Produit</Form.Label>
                                <div className="input-group">
                                    <span className="input-group-text">
                                        <ShoppingBag size={18} />
                                    </span>
                                    <Form.Control
                                        type="text"
                                        name="name"
                                        value={values.name}
                                        onChange={handleChange}
                                        placeholder="Entrer nom produit"
                                        required
                                    />
                                </div>
                                {errors.name && <Form.Text className="text-danger">{errors.name}</Form.Text>}
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Référence</Form.Label>
                                <div className="input-group">
                                    <span className="input-group-text">
                                        <Tag size={18} />
                                    </span>
                                    <Form.Control
                                        type="text"
                                        name="sku"
                                        value={values.sku}
                                        onChange={handleChange}
                                        placeholder="Entrer ref produit"
                                        required
                                    />
                                </div>
                                {errors.sku && <Form.Text className="text-danger">{errors.sku}</Form.Text>}
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Prix</Form.Label>
                                <InputGroup>
                                    <InputGroup.Text>
                                        {/* <DollarSign size={18} /> */}
                                    </InputGroup.Text>
                                    <Form.Control
                                        type="text"
                                        name="regular_price"
                                        value={values.regular_price}
                                        onChange={handleChange}
                                        placeholder="Entrer le prix"
                                        required
                                    />
                                </InputGroup>
                                {errors.regular_price && <Form.Text className="text-danger">{errors.regular_price}</Form.Text>}
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Catégorie</Form.Label>
                                <InputGroup>
                                    <InputGroup.Text>
                                        <Layers size={18} />
                                    </InputGroup.Text>
                                    <Form.Control
                                        type="text"
                                        name="category"
                                        value={values.category}
                                        onChange={handleChange}
                                        placeholder="Entrer categorie"
                                    />
                                </InputGroup>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Dimensions</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="dimensions"
                                    value={values.dimensions}
                                    onChange={handleChange}
                                    placeholder="Enrter les dimensions (e.g., 10x20x5 cm)"
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <Form.Group className="mb-4">
                        <Form.Label>Nom en Chinois</Form.Label>
                        <InputGroup>
                            <InputGroup.Text>
                                <FileText size={18} />
                            </InputGroup.Text>
                            <Form.Control
                                as="textarea"
                                name="description"
                                value={values.description}
                                onChange={handleChange}
                                placeholder="Entrer le nom du produit en chinois"
                                style={{ height: '100px' }}
                            />
                        </InputGroup>
                    </Form.Group>

                    <Form.Group className="mb-4">
                        <Form.Label>Images Produits</Form.Label>

                        <div className="d-flex flex-wrap gap-3 mb-3">
                            {images.map((image, index) => (
                                <div key={index} className="position-relative" style={{ width: '120px' }}>
                                    <div
                                        className={`border rounded p-1 ${mainImageIndex === index ? 'border-primary' : ''}`}
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => setAsMainImage(index)}
                                    >
                                        <img
                                            src={image.isNew ? image.path : getFullImageUrl(image.path)}
                                            alt={`Product ${index + 1}`}
                                            className="img-fluid"
                                            style={{ height: '100px', objectFit: 'cover', width: '100%' }}
                                            onError={(e) => {
                                                console.error("Error loading image:", e);
                                                e.target.onerror = null;
                                                e.target.src = `data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><circle cx="8.5" cy="8.5" r="1.5"></circle><polyline points="21 15 16 10 5 21"></polyline></svg>`;
                                            }}
                                        />
                                        {mainImageIndex === index && (
                                            <Badge className="position-absolute top-0 start-0 m-1 bg-primary">Main</Badge>
                                        )}
                                        <Button
                                            variant="danger"
                                            size="sm"
                                            className="position-absolute top-0 end-0 m-1 p-0"
                                            style={{ width: '20px', height: '20px' }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleRemoveImage(index);
                                            }}
                                        >
                                            <X size={12} />
                                        </Button>
                                    </div>
                                </div>
                            ))}

                            {/* Add image button */}
                            <div className="border rounded p-1 d-flex justify-content-center align-items-center"
                                style={{ width: '120px', height: '120px', cursor: 'pointer' }}>
                                <label htmlFor={isEditMode ? "edit-product-image" : "product-image"} style={{ cursor: 'pointer' }}>
                                    <div className="text-center">
                                        <Plus size={24} className="mb-2 text-primary" />
                                        <div className="small text-muted">Ajouter Image</div>
                                    </div>
                                    <input
                                        type="file"
                                        id={isEditMode ? "edit-product-image" : "product-image"}
                                        accept="image/jpeg,image/png,image/webp"
                                        style={{ display: 'none' }}
                                        onChange={isEditMode ? handleUploadImage : handleImageChange}
                                        multiple={!isEditMode}
                                    />
                                </label>
                            </div>
                        </div>

                        <Form.Text className="text-muted">
                            Téléchargez des images du produit. La première image ou l'image sélectionnée sera utilisée comme image principale du produit.
                        </Form.Text>
                    </Form.Group>

                    <Row className="mb-3">
                        <Col>
                            {values.wpProductId && (
                                <Alert variant="info">
                                    <strong>Produit synchronisé avec WordPress</strong>
                                    <p className="mb-0">ID WordPress: {values.wpProductId}</p>
                                </Alert>
                            )}
                        </Col>
                    </Row>

                    <div className="d-flex justify-content-end gap-2">
                        <Button
                            variant="outline-secondary"
                            onClick={onCancel}
                            disabled={loading || imageUploading || syncingWithWP}
                        >
                            <X size={16} className="me-1" />
                            Annuler
                        </Button>
                        <Button
                            variant="primary"
                            type="submit"
                            disabled={loading || imageUploading || syncingWithWP}
                        >
                            {loading || syncingWithWP ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    {syncingWithWP ? 'Synchronisation avec WordPress...' : (isEditMode ? 'Modification...' : 'Création...')}
                                </>
                            ) : (
                                <>
                                    <Save size={16} className="me-1" />
                                    {isEditMode ? 'Modifier produit' : 'Créer Produit'}
                                </>
                            )}
                        </Button>
                    </div>
                </Form>
            </Card.Body>
        </Card>
    );
};

export default ProductForm;